import XLSX from 'sheetjs-style';

// Mirror of SQL Server schema for portalhc.product
// - varchar/nvarchar: validated by max length (in chars)
// - int: validated as integer when provided
// - nvarchar(-1) / varchar(-1) => treated as unlimited
const PRODUCT_SCHEMA = {
    sap_code: { dataType: 'nvarchar', maxLen: 10, required: true },
    operation_group: { dataType: 'nvarchar', maxLen: 50 },
    product_segment_id: { dataType: 'int' },
    description: { dataType: 'nvarchar', maxLen: 100, required: true },
    inv_niv: { dataType: 'varchar', maxLen: 20 },
    flow_o2: { dataType: 'varchar', maxLen: 20 },
    adult_child: { dataType: 'nvarchar', maxLen: 8 },
    weight: { dataType: 'nvarchar', maxLen: 10 },
    eletrical_supply: { dataType: 'nvarchar', maxLen: 5 },
    generic: { dataType: 'nvarchar', maxLen: 50 },
    product_category_id: { dataType: 'int' },
    product_subcategory_id: { dataType: 'int' },
    type: { dataType: 'varchar', maxLen: 8 },
    layout: { dataType: 'nvarchar', maxLen: 1 },
    indicate_rule: { dataType: 'nvarchar', maxLen: null },
    size: { dataType: 'varchar', maxLen: 100 },
    explanation: { dataType: 'nvarchar', maxLen: null },
    technical_information: { dataType: 'nvarchar', maxLen: null },
    explanation1: { dataType: 'nvarchar', maxLen: null },
};

const toNullableTrimmedString = (value) => {
    if (value === null || value === undefined) return null;
    const text = `${value}`.trim();
    return text.length === 0 ? null : text;
};

const isPlainObject = (v) =>
    !!v && typeof v === 'object' && !Array.isArray(v) && !(v instanceof Date);

const coerceTextValue = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'string') return toNullableTrimmedString(value);
    if (typeof value === 'number' || typeof value === 'boolean')
        return toNullableTrimmedString(String(value));
    if (value instanceof Date)
        return toNullableTrimmedString(value.toISOString());
    if (Array.isArray(value) || isPlainObject(value))
        return { invalidType: true };
    return toNullableTrimmedString(String(value));
};

const isIntegerLike = (value) => {
    if (value === null || value === undefined || value === '') return true;
    if (typeof value === 'number')
        return Number.isFinite(value) && Math.trunc(value) === value;
    const text = `${value}`.trim();
    if (text.length === 0) return true;
    if (!/^-?\d+$/.test(text)) return false;
    const n = Number(text);
    return Number.isFinite(n) && Math.trunc(n) === n;
};

const truncateForMessage = (value, maxLen = 48) => {
    if (value === null || value === undefined) return '';
    const text = `${value}`.replace(/\s+/g, ' ').trim();
    if (!text) return '';
    if (text.length <= maxLen) return text;
    return `${text.slice(0, maxLen)}…`;
};

const buildValidationMessage = ({ totalErrors, issuesByColumn }) => {
    const columns = Object.keys(issuesByColumn || {}).sort();
    const lines = [];

    lines.push(`Planilha inválida: ${totalErrors} erro(s).`);
    if (columns.length > 0) {
        lines.push(
            `Colunas com problema (${columns.length}): ${columns.join(', ')}`
        );
    }
    lines.push('');
    lines.push('Resumo por coluna:');

    for (const col of columns) {
        const bucket = issuesByColumn[col] || {};
        const parts = [];

        if (bucket.required?.count)
            parts.push(`${bucket.required.count} obrigatório(s) vazio(s)`);
        if (bucket.int?.count)
            parts.push(`${bucket.int.count} inválido(s) (esperado int)`);
        if (bucket.decimalSeparator?.count)
            parts.push(
                `${bucket.decimalSeparator.count} usa(m) vírgula em número (use ponto)`
            );
        if (bucket.maxLen?.count)
            parts.push(`${bucket.maxLen.count} excede(m) tamanho`);
        if (bucket.invalidType?.count)
            parts.push(`${bucket.invalidType.count} tipo inválido`);

        const examples = [];

        const pushExamples = (items, formatter) => {
            if (!Array.isArray(items)) return;
            for (const ex of items) {
                if (examples.length >= 3) break;
                examples.push(formatter(ex));
            }
        };

        pushExamples(bucket.required?.examples, (ex) => `L${ex.row}`);
        pushExamples(
            bucket.int?.examples,
            (ex) => `L${ex.row}="${truncateForMessage(ex.value)}"`
        );
        pushExamples(
            bucket.decimalSeparator?.examples,
            (ex) => `L${ex.row}="${truncateForMessage(ex.value)}"`
        );
        pushExamples(
            bucket.maxLen?.examples,
            (ex) =>
                `L${ex.row}="${truncateForMessage(ex.value)}" (${ex.actualLen}/${ex.maxLen
                })`
        );
        pushExamples(bucket.invalidType?.examples, (ex) => `L${ex.row}`);

        const base = `- ${col}: ${parts.join('; ') || 'erro(s)'}${examples.length ? `. Ex.: ${examples.join(' | ')}` : ''
            }`;
        lines.push(base);
    }

    return lines.join('\n');
};

export const validateProductsForUpload = async ({
    products,
    maxErrors = 20,
    onProgress,
}) => {
    const safeProducts = Array.isArray(products) ? products : [];
    const total = safeProducts.length;
    const schemaEntries = Object.entries(PRODUCT_SCHEMA);

    const issuesByColumn = {};
    let totalErrors = 0;
    let totalExampleCount = 0;

    const bumpIssue = (col, type, example) => {
        if (!col) return;
        if (!issuesByColumn[col]) issuesByColumn[col] = {};
        if (!issuesByColumn[col][type])
            issuesByColumn[col][type] = { count: 0, examples: [] };
        issuesByColumn[col][type].count += 1;

        if (
            example &&
            totalExampleCount < maxErrors &&
            issuesByColumn[col][type].examples.length < 2
        ) {
            issuesByColumn[col][type].examples.push(example);
            totalExampleCount += 1;
        }
    };

    const progressEvery = 25;
    const yieldEvery = 100;

    for (let i = 0; i < total; i++) {
        const row = safeProducts[i] ?? {};
        const excelRow = Number(row?.__rowNumber) || i + 2;

        for (const [col, spec] of schemaEntries) {
            if (!spec?.required) continue;
            const coerced = coerceTextValue(row?.[col]);
            if (coerced === null) {
                totalErrors++;
                bumpIssue(col, 'required', { row: excelRow });
            }
        }

        for (const [col, spec] of schemaEntries) {
            const dataType = spec?.dataType;
            const maxLen = spec?.maxLen;
            const value = row?.[col];

            if (dataType === 'int') {
                if (!isIntegerLike(value)) {
                    totalErrors++;
                    bumpIssue(col, 'int', { row: excelRow, value });
                }
                continue;
            }

            const coerced = coerceTextValue(value);
            if (coerced && coerced.invalidType) {
                totalErrors++;
                bumpIssue(col, 'invalidType', { row: excelRow });
                continue;
            }

            const text = typeof coerced === 'string' ? coerced : null;
            if (!text) continue;

            if (col === 'size') {
                if (/(\d),(\d)/.test(text)) {
                    totalErrors++;
                    bumpIssue(col, 'decimalSeparator', { row: excelRow, value: text });
                }
            }
            if (typeof maxLen === 'number' && maxLen > 0 && text.length > maxLen) {
                totalErrors++;
                bumpIssue(col, 'maxLen', {
                    row: excelRow,
                    value: text,
                    actualLen: text.length,
                    maxLen,
                    dataType,
                });
            }
        }

        if (
            typeof onProgress === 'function' &&
            (i === total - 1 || (i + 1) % progressEvery === 0)
        ) {
            onProgress({ current: i + 1, total });
        }

        if ((i + 1) % yieldEvery === 0) {
            // eslint-disable-next-line no-await-in-loop
            await new Promise((r) => setTimeout(r, 0));
        }
    }

    if (totalErrors > 0) {
        throw new Error(buildValidationMessage({ totalErrors, issuesByColumn }));
    }
};

const toTrimmedLowerTokens = (row) =>
    (Array.isArray(row) ? row : [])
        .map((cell) => `${cell ?? ''}`.trim().toLowerCase())
        .filter((t) => t.length > 0);

const normalizeSizeValue = (value) => {
    if (value === null || value === undefined) return value;
    if (typeof value !== 'string') return value;

    // Normaliza somente vírgulas entre dígitos: "9,6" -> "9.6" (mantém unidades, se houver)
    // Ex.: "9,6 m³" -> "9.6 m³"
    return value.replace(/(\d),(\d)/g, '$1.$2');
};

export const looksLikeTypeRow = (row) => {
    const tokens = toTrimmedLowerTokens(row);
    if (tokens.length === 0) return false;

    const typeLikeRegex = new RegExp(
        [
            '^(var)?char\\(\\d+\\)$',
            '^nvarchar\\(\\d+\\)$',
            '^varchar\\(\\d+\\)$',
            '^char\\(\\d+\\)$',
            '^int$',
            '^bigint$',
            '^smallint$',
            '^tinyint$',
            '^bit$',
            '^date$',
            '^datetime$',
            '^datetime2$',
            '^smalldatetime$',
            '^time$',
            '^decimal\\(\\d+,\\d+\\)$',
            '^numeric\\(\\d+,\\d+\\)$',
            '^float$',
            '^real$',
            '^money$',
            '^smallmoney$',
            '^uniqueidentifier$',
            '^text$',
            '^ntext$',
            '^xml$',
            '^not null$',
            '^null$',
            '\\(not null\\)$',
        ].join('|')
    );

    const typeLikeCount = tokens.filter((t) => typeLikeRegex.test(t)).length;
    return typeLikeCount / tokens.length >= 0.6;
};

const getFirstSheet = (arrayBuffer) => {
    const workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });
    const firstSheetName = workbook.SheetNames?.[0];
    if (!firstSheetName) throw new Error('A planilha não possui abas.');

    const worksheet = workbook.Sheets[firstSheetName];
    if (!worksheet) throw new Error('A primeira aba está vazia ou inválida.');

    return { firstSheetName, worksheet };
};

const getNonEmptyRows = (worksheet) => {
    const rawRows = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        defval: '',
    });
    return (rawRows || []).filter(
        (row) =>
            Array.isArray(row) && row.some((cell) => `${cell ?? ''}`.trim() !== '')
    );
};

export const buildPrecheckFromArrayBuffer = ({
    arrayBuffer,
    normalizeColumnName,
    requiredColumns,
    previewRowCount = 3,
}) => {
    const { firstSheetName, worksheet } = getFirstSheet(arrayBuffer);
    const nonEmptyRows = getNonEmptyRows(worksheet);

    if (nonEmptyRows.length === 0) {
        return {
            sheetName: firstSheetName,
            headers: [],
            rows: [],
            totalRows: 0,
            missingColumns: [],
        };
    }

    const headerIndex =
        nonEmptyRows.length >= 2 && looksLikeTypeRow(nonEmptyRows[0]) ? 1 : 0;
    const headerRow = Array.isArray(nonEmptyRows[headerIndex])
        ? nonEmptyRows[headerIndex]
        : [];
    const previewDataRows = nonEmptyRows.slice(
        headerIndex + 1,
        headerIndex + 1 + previewRowCount
    );
    const totalRows = Math.max(nonEmptyRows.length - (headerIndex + 1), 0);

    const headerLabels = headerRow
        .map((h) => `${h ?? ''}`.trim())
        .filter((h) => h.length > 0);

    if (headerLabels.length === 0) {
        throw new Error('Não foi possível identificar o cabeçalho da planilha.');
    }

    const normalizedHeaderSet = new Set(
        headerLabels.map(normalizeColumnName).filter(Boolean)
    );
    const missing = requiredColumns.filter(
        (c) => !normalizedHeaderSet.has(normalizeColumnName(c))
    );

    const colCount = Math.max(
        headerRow.length,
        ...previewDataRows.map((row) => (Array.isArray(row) ? row.length : 0)),
        0
    );

    const headers = Array.from({ length: colCount }).map((_, idx) => {
        const label = `${headerRow?.[idx] ?? ''}`.trim();
        return label || `Coluna ${idx + 1}`;
    });

    const formatCell = (value) => {
        if (value instanceof Date) return value.toLocaleDateString('pt-BR');
        if (value === null || value === undefined) return '';
        return `${value}`;
    };

    const rows = previewDataRows.map((row) => {
        const safeRow = Array.isArray(row) ? row : [];
        return headers.map((_, idx) => formatCell(safeRow[idx]));
    });

    return {
        sheetName: firstSheetName,
        headers,
        rows,
        totalRows,
        missingColumns: missing,
    };
};

export const parseProductsFromArrayBuffer = ({
    arrayBuffer,
    normalizeColumnName,
    requiredColumns,
}) => {
    const { firstSheetName, worksheet } = getFirstSheet(arrayBuffer);
    const nonEmptyRows = getNonEmptyRows(worksheet);

    if (nonEmptyRows.length === 0) throw new Error('A planilha está vazia.');

    const headerIndex =
        nonEmptyRows.length >= 2 && looksLikeTypeRow(nonEmptyRows[0]) ? 1 : 0;
    const headerRow = Array.isArray(nonEmptyRows[headerIndex])
        ? nonEmptyRows[headerIndex]
        : [];
    const dataRows = nonEmptyRows.slice(headerIndex + 1);

    const normalizedHeaders = headerRow.map((h) => normalizeColumnName(h));
    const normalizedHeaderSet = new Set(normalizedHeaders.filter(Boolean));
    const missing = requiredColumns.filter(
        (c) => !normalizedHeaderSet.has(normalizeColumnName(c))
    );

    if (missing.length > 0) {
        throw new Error(`Faltam colunas obrigatórias: ${missing.join(', ')}`);
    }

    const formatCell = (value) => {
        if (value instanceof Date) return value.toISOString();
        if (value === null || value === undefined) return '';
        return value;
    };

    const normalizations = {
        size: { count: 0, commaToDot: 0, numericDecimal: 0, examples: [] },
    };

    const products = dataRows
        .map((row, idx) => {
            const safeRow = Array.isArray(row) ? row : [];
            const obj = {};
            normalizedHeaders.forEach((key, colIdx) => {
                if (!key) return;
                obj[key] = formatCell(safeRow[colIdx]);
            });

            if (Object.prototype.hasOwnProperty.call(obj, 'size')) {
                const before = obj.size;

                if (typeof before === 'string') {
                    const after = normalizeSizeValue(before);
                    if (after !== before) {
                        normalizations.size.count += 1;
                        normalizations.size.commaToDot += 1;
                        if (normalizations.size.examples.length < 3) {
                            const excelRow = headerIndex + 2 + idx;
                            normalizations.size.examples.push(
                                `L${excelRow}: ${before} → ${after}`
                            );
                        }
                        obj.size = after;
                    }
                }

                // 2) Se vier como número com casas decimais, converte para string (com ponto),
                // para garantir payload consistente e permitir o aviso ao usuário.
                if (typeof before === 'number' && Number.isFinite(before)) {
                    const isDecimal = Math.trunc(before) !== before;
                    if (isDecimal) {
                        const after = String(before);
                        normalizations.size.count += 1;
                        normalizations.size.numericDecimal += 1;
                        if (normalizations.size.examples.length < 3) {
                            const excelRow = headerIndex + 2 + idx;
                            normalizations.size.examples.push(
                                `L${excelRow}: ${before} (numérico) → ${after}`
                            );
                        }
                        obj.size = after;
                    }
                }
            }

            // for user-friendly validation messages (1-based Excel row number)
            obj.__rowNumber = headerIndex + 2 + idx;
            return obj;
        })
        .filter((obj) => Object.keys(obj).length > 0);

    if (products.length === 0)
        throw new Error('Nenhuma linha de produto encontrada para enviar.');

    return {
        sheetName: firstSheetName,
        products,
        totalRows: products.length,
        normalizations,
    };
};

export const parseProductsFromFile = async ({
    file,
    normalizeColumnName,
    requiredColumns,
}) => {
    const arrayBuffer = await file.arrayBuffer();
    return parseProductsFromArrayBuffer({
        arrayBuffer,
        normalizeColumnName,
        requiredColumns,
    });
};
